/**
 * localStorage Validation Utilities
 * 
 * Validates and sanitizes data stored in localStorage to prevent
 * XSS attacks, data manipulation, and application errors.
 */

/**
 * Validates that a value is a positive integer within valid range
 * @param {*} value - Value to validate
 * @returns {number|null} - Validated integer or null if invalid
 */
export const validateItemId = (value) => {
  // Convert to number if string
  let numValue;
  if (typeof value === 'string') {
    numValue = parseInt(value, 10);
    if (isNaN(numValue)) {
      return null;
    }
  } else if (typeof value === 'number') {
    numValue = value;
  } else {
    return null;
  }

  // Must be positive integer
  if (!Number.isInteger(numValue)) {
    return null;
  }

  // Must be greater than 0
  if (numValue <= 0) {
    return null;
  }

  // Must be within valid range (max 32-bit signed integer)
  if (numValue > 2147483647) {
    return null;
  }

  return numValue;
};

/**
 * Validates an array of item IDs
 * @param {*} value - Value to validate (should be array)
 * @returns {number[]} - Array of validated item IDs
 */
export const validateItemIdArray = (value) => {
  if (!Array.isArray(value)) {
    return [];
  }

  const validatedIds = [];
  for (const item of value) {
    const validId = validateItemId(item);
    if (validId !== null) {
      validatedIds.push(validId);
    }
  }

  // Remove duplicates
  return [...new Set(validatedIds)];
};

/**
 * Safely gets and validates item IDs from localStorage
 * @param {string} key - localStorage key
 * @returns {number[]} - Array of validated item IDs
 */
export const getValidatedItemIds = (key) => {
  try {
    const stored = localStorage.getItem(key);
    if (!stored) {
      return [];
    }

    const parsed = JSON.parse(stored);
    return validateItemIdArray(parsed);
  } catch (error) {
    console.error(`Error validating localStorage key "${key}":`, error);
    // Clear corrupted data
    try {
      localStorage.removeItem(key);
    } catch (e) {
      // Ignore errors when removing
    }
    return [];
  }
};

/**
 * Safely sets item IDs to localStorage with validation
 * @param {string} key - localStorage key
 * @param {number[]} itemIds - Array of item IDs to store
 * @returns {boolean} - True if successful, false otherwise
 */
export const setValidatedItemIds = (key, itemIds) => {
  try {
    const validated = validateItemIdArray(itemIds);
    localStorage.setItem(key, JSON.stringify(validated));
    return true;
  } catch (error) {
    // Handle quota exceeded error
    if (error.name === 'QuotaExceededError') {
      console.error(`localStorage quota exceeded for key "${key}"`);
      return false;
    }
    console.error(`Error setting localStorage key "${key}":`, error);
    return false;
  }
};

/**
 * Validates and sanitizes favorites from localStorage
 * @returns {number[]} - Array of validated favorite item IDs
 */
export const getValidatedFavorites = () => {
  return getValidatedItemIds('alertique_favorites');
};

/**
 * Sets favorites to localStorage with validation
 * @param {number[]} itemIds - Array of favorite item IDs
 * @returns {boolean} - True if successful, false otherwise
 */
export const setValidatedFavorites = (itemIds) => {
  return setValidatedItemIds('alertique_favorites', itemIds);
};

/**
 * Validates and sanitizes pending track items from localStorage
 * @returns {number[]} - Array of validated pending track item IDs
 */
export const getValidatedPendingTrackItems = () => {
  return getValidatedItemIds('pending_track_items');
};

/**
 * Sets pending track items to localStorage with validation
 * @param {number[]} itemIds - Array of pending track item IDs
 * @returns {boolean} - True if successful, false otherwise
 */
export const setValidatedPendingTrackItems = (itemIds) => {
  return setValidatedItemIds('pending_track_items', itemIds);
};
